import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Globe, Heart, Users, Film } from 'lucide-react';
import { AnimatedSection, AnimatedText, CountUp } from '@/components/AnimatedComponents';
import VideoPlayer from '@/components/VideoPlayer';

const impactStats = [
  { icon: Users, number: 2000000, suffix: '+', label: 'People Reached' },
  { icon: Globe, number: 45, suffix: '', label: 'Countries Screened' },
  { icon: Heart, number: 500000, suffix: '$', label: 'Funds Raised' },
];

export default function FilmsForHumanity() {
  return (
    <main data-testid="films-for-humanity-page">
      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center pt-20">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/tetfj4m6_Terry_FFH_photo_01.jpg)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
        
        <div className="container-wide relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="caption mb-4"
          >
            Documentary Filmmaking
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-8xl font-bold tracking-tighter mb-6"
          >
            Real Human<br />
            <span className="text-[#007AFF]">Stories</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[#A1A1AA] text-lg md:text-xl max-w-xl mb-8"
          >
            Documentary filmmaking that amplifies untold stories, 
            drives social change, and creates lasting humanitarian impact.
          </motion.p>
        </div>
      </section>

      {/* Featured Documentary: Washed Away */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="washed-away-section">
        <div className="container-wide">
          <AnimatedSection className="mb-16">
            <p className="caption mb-4">Featured Documentary</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter">
              Washed Away
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <AnimatedSection>
              <VideoPlayer 
                vimeoId="1149954429"
                posterImage="https://customer-assets.emergentagent.com/job_site-clone-platform/artifacts/5sx26xyf_thumb%20%285%29.jpg"
                title="Washed Away Documentary"
                className="rounded-sm mb-8"
              />
              
              {/* Streaming info */}
              <a 
                href="https://washedaway.au" 
                target="_blank" 
                rel="noopener noreferrer"
                className="block p-4 border border-[#007AFF]/30 bg-[#007AFF]/5 hover:bg-[#007AFF]/10 transition-colors"
              >
                <p className="text-[#007AFF] text-xs font-bold uppercase tracking-wider mb-1">
                  Streaming Now
                </p>
                <p className="text-white text-sm flex items-center gap-2">
                  washedaway.au
                  <ArrowRight size={14} />
                </p>
              </a>
            </AnimatedSection>

            <AnimatedSection delay={0.2}>
              <div className="space-y-8">
                <div className="quote-block">
                  <p className="text-xl md:text-2xl text-white italic leading-relaxed">
                    &quot;The waters receded, but the struggle didn&apos;t. We support each other; we support our neighbours and let them know they are not forgotten.&quot;
                  </p>
                  <p className="mt-4 text-[#A1A1AA]">— Mick Fanning, 3x World Surfing Champion</p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-4">The Story</h3>
                  <p className="text-[#A1A1AA] leading-relaxed mb-4">
                    In February 2022, a &apos;monster&apos; flood inundated the Northern Rivers with more than 14 metres of water, submerging thousands of homes overnight. On-the-ground first responders could only watch and wait, hamstrung by red tape and a lack of resources — leaving scores of people trapped in their homes, cut off from assistance.
                  </p>
                  <p className="text-[#A1A1AA] leading-relaxed mb-4">
                    Washed Away follows the locals who stepped in to help, risking everything to save a stranger, and reveals a community on its knees that triumphed over tragedy — celebrating the heroes and paying tribute to those who lost their lives.
                  </p>
                  <p className="text-[#A1A1AA] leading-relaxed">
                    This documentary not only celebrates the resilience of the human spirit but also calls for greater accountability and improved crisis management strategies in areas destined to flood again.
                  </p>
                </div>

                <div>
                  <h3 className="text-2xl font-bold mb-4">Impact</h3>
                  <ul className="space-y-3 text-[#A1A1AA]">
                    <li className="flex items-start gap-3">
                      <span className="text-[#007AFF]">•</span>
                      50% of profits support flood recovery through Hands and Hearts
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#007AFF]">•</span>
                      Featuring music by Silverchair, Goanna, Pete Murray, Tex Perkins
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#007AFF]">•</span>
                      Supporting repairs of 100+ flood-damaged homes
                    </li>
                  </ul>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Support the Mission */}
      <section className="py-24 md:py-32 bg-[#0C0C0C]" data-testid="impact-stats-section">
        <div className="container-wide">
          <AnimatedSection className="text-center mb-16">
            <p className="caption mb-4">Help Us Make an Impact</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter">
              Support the Mission
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto mt-6">
              Washed Away is more than a film — it&apos;s a movement. We&apos;re an independent production 
              working to get this story seen. Every view, every share, every conversation helps 
              the communities still rebuilding.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <AnimatedSection delay={0.1} className="text-center p-8 border border-[#27272A]">
              <Film className="w-10 h-10 text-[#007AFF] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Watch the Film</h3>
              <p className="text-[#A1A1AA] text-sm">Stream now at washedaway.au and support ongoing recovery efforts.</p>
            </AnimatedSection>
            <AnimatedSection delay={0.2} className="text-center p-8 border border-[#27272A]">
              <Heart className="w-10 h-10 text-[#007AFF] mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Share the Story</h3>
              <p className="text-[#A1A1AA] text-sm">Help us reach more people. Every share brings awareness to communities still rebuilding.</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* The Team */}
      <section className="py-24 md:py-32 bg-[#0C0C0C]" data-testid="team-section">
        <div className="container-wide">
          <AnimatedSection className="text-center mb-16">
            <p className="caption mb-4">The Creatives</p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-6">
              Thank You
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto mb-8">
              Without these amazing creatives, Washed Away would not have been possible. 
              A heartfelt thank you to everyone who brought this story to life.
            </p>
            <p className="text-[#007AFF] text-sm font-bold uppercase tracking-wider mb-2">Featuring</p>
            <p className="text-[#A1A1AA] text-sm max-w-3xl mx-auto">Michael Barnes, Mick Fanning, Renee Honey, Caz Goulding, Ryan Hipwood, Jarrah Tutton, Michelle Bryant, Kate Coxel, Angus Robb, Alana Wilkenson</p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <AnimatedSection delay={0.1}>
              <div className="text-center">
                <p className="text-[#007AFF] text-sm font-bold uppercase tracking-wider mb-2">Directors</p>
                <p className="text-white font-medium">Terry Webster</p>
                <p className="text-white font-medium">Stuart McKay</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.2}>
              <div className="text-center">
                <p className="text-[#007AFF] text-sm font-bold uppercase tracking-wider mb-2">Writer</p>
                <p className="text-white font-medium">Rebecca Matkovic-Helou</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.3}>
              <div className="text-center">
                <p className="text-[#007AFF] text-sm font-bold uppercase tracking-wider mb-2">Narrator</p>
                <p className="text-white font-medium">David Field</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.4}>
              <div className="text-center">
                <p className="text-[#007AFF] text-sm font-bold uppercase tracking-wider mb-2">Producers</p>
                <p className="text-white font-medium">Frank Coorey</p>
                <p className="text-white font-medium">Lyndall Murray</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.5}>
              <div className="text-center">
                <p className="text-[#007AFF] text-sm font-bold uppercase tracking-wider mb-2">Production</p>
                <p className="text-white font-medium">Free Flow Films</p>
                <p className="text-white font-medium">SOCIALFUEL</p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={0.5}>
              <div className="text-center">
                <p className="text-[#007AFF] text-sm font-bold uppercase tracking-wider mb-2">Music</p>
                <p className="text-[#A1A1AA] text-sm">Silverchair, Ziggy Alberts, Goanna, Tex Perkins, Pete Murray, Noiseworks, Ben Jansz</p>
              </div>
            </AnimatedSection>
          </div>

          <AnimatedSection delay={0.7} className="text-center mt-12">
            <a 
              href="https://www.imdb.com/title/tt39048607/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#007AFF] hover:underline"
            >
              View full credits on IMDb →
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0C0C0C] border-t border-[#27272A]">
        <div className="container-wide text-center">
          <AnimatedSection>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter mb-6">
              Have a story that needs to be told?
            </h2>
            <p className="text-[#A1A1AA] text-lg max-w-xl mx-auto mb-8">
              I&apos;m passionate about documentary projects that create meaningful 
              social impact. Let&apos;s discuss your vision.
            </p>
            <Link 
              to="/contact" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="collaborate-cta-btn"
            >
              Let&apos;s Collaborate
              <ArrowRight size={18} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
}
